import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { royalcaninDogProducts, royalcaninCatProducts, eukanubaProducts, simparicaProducts, nexgardProducts, bravectoProducts, purinaProducts, bobmartinProducts } from '../ProductRetrieval'
import { searchProducts } from '../../Utils/searchProducts'
import './Search.css'

const allProducts = [
  ...royalcaninDogProducts.map(p => ({ ...p, petType: 'Dog' })),
  ...royalcaninCatProducts.map(p => ({ ...p, petType: 'Cat' })),
  ...eukanubaProducts.map(p => ({ ...p, petType: 'Dog' })),
  ...nexgardProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  ...simparicaProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  // ...beenoProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  ...bravectoProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  ...purinaProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  ...bobmartinProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
]

const formatZAR = (amount) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2,
      }).format(amount)
    : amount

const Search = ({ compact = false }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  const handleSearch = useCallback((term) => {
    setQuery(term)
    if (term.trim().length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }
    const matches = searchProducts(allProducts, term, ['name', 'description', 'category', 'breed', 'brand', 'brands'])
    const unique = []
    const seen = new Set()
    for (const p of matches) {
      const key = p.name.replace(/\(\d+\.?\d*\s*(kg|g)\)$/, '').trim()
      if (!seen.has(key)) {
        seen.add(key)
        unique.push(p)
      }
    }
    setResults(unique.slice(0, 12))
    setIsOpen(true)
    setActiveTab('all')
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.trim()) {
      setIsOpen(false)
      navigate(`/PetFood?q=${encodeURIComponent(query.trim())}`)
    }
  }

  const handleSelect = (product) => {
    setIsOpen(false)
    setQuery('')
    navigate(`/Product/${product.id}`)
  }

  const filteredResults = activeTab === 'all'
    ? results
    : results.filter(p => p.petType === activeTab)

  const dogCount = results.filter(p => p.petType === 'Dog').length
  const catCount = results.filter(p => p.petType === 'Cat').length

  return (
    <div className="search-wrapper-global" ref={wrapperRef}>
      <form onSubmit={handleSubmit} className={`search-form-global ${compact ? 'search-compact' : ''}`}>
        <svg className="search-icon-global" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder="Search products..."
          className="search-input-global"
          autoComplete="off"
        />
        {query && (
          <button type="button" className="search-clear-global" onClick={() => { setQuery(''); setResults([]); setIsOpen(false) }}>
            &times;
          </button>
        )}
      </form>

      {isOpen && results.length > 0 && (
        <div className="search-dropdown-global">
          <div className="search-tabs-global">
            <button
              className={`search-tab-global ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({results.length})
            </button>
            {dogCount > 0 && (
              <button
                className={`search-tab-global ${activeTab === 'Dog' ? 'active' : ''}`}
                onClick={() => setActiveTab('Dog')}
              >
                Dogs ({dogCount})
              </button>
            )}
            {catCount > 0 && (
              <button
                className={`search-tab-global ${activeTab === 'Cat' ? 'active' : ''}`}
                onClick={() => setActiveTab('Cat')}
              >
                Cats ({catCount})
              </button>
            )}
          </div>

          <div className="search-results-global">
            {filteredResults.map((product) => (
              <div
                key={product.id}
                className="search-result-item-global"
                onClick={() => handleSelect(product)}
              >
                <div className="search-result-img-global">
                  {product.image ? (
                    <img src={product.image} alt={product.name} />
                  ) : (
                    <div className="search-result-no-img">No img</div>
                  )}
                </div>
                <div className="search-result-info-global">
                  <span className="search-result-name-global">{product.name}</span>
                  <span className="search-result-meta-global">
                    <span className="search-result-pet-global">{product.petType}</span>
                    {product.category && <span className="search-result-cat-global">{product.category}</span>}
                  </span>
                </div>
                <span className="search-result-price-global">{formatZAR(product.price)}</span>
              </div>
            ))}
          </div>

          <button
            className="search-view-all-global"
            onClick={() => { setIsOpen(false); navigate(`/PetFood?q=${encodeURIComponent(query.trim())}`) }}
          >
            View all results for "{query}"
          </button>
        </div>
      )}

      {isOpen && query.length >= 2 && results.length === 0 && (
        <div className="search-dropdown-global">
          <div className="search-empty-global">
            No products found for "{query}"
          </div>
        </div>
      )}
    </div>
  )
}

export default Search
